import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from './hooks/useToast';
import { useWallet } from './WalletContext';
import { fetchGameAnkData } from './services/gameService';
import './GameEntry.css';

function StarlineSinglePannaGame({ gameName, onBack }) {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { walletBalance, updateWalletBalance } = useWallet();
  const [availableDigits, setAvailableDigits] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [filteredDigits, setFilteredDigits] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedDigit, setSelectedDigit] = useState('');
  const [points, setPoints] = useState('');
  const [showEntryAdded, setShowEntryAdded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const dropdownRef = useRef(null);

  const openTime = location.state?.openTime || '09:00';
  const closeTime = location.state?.closeTime || '18:00';
  const gameType = location.state?.gameTypeId || '';
  const gameId = location.state?.gameId || '';
  const gameNameFromState = location.state?.gameName || '';
  const gameTypeLabel = 'Single Panna';

  // Fetch game data from API
  useEffect(() => {
    const fetchGameData = async () => {
      try {
        setLoading(true);
        const gameData = await fetchGameAnkData();
        
        // Extract single_panna digits for this game
        if (gameData.single_panna && Array.isArray(gameData.single_panna)) {
          setAvailableDigits(gameData.single_panna);
        }
        setError('');
      } catch (err) {
        setError('Failed to load game data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchGameData();
  }, []);

  // Filter digits based on search input
  useEffect(() => {
    if (!searchInput.trim()) {
      setFilteredDigits([]);
      return;
    }

    if (!Array.isArray(availableDigits) || availableDigits.length === 0) {
      setFilteredDigits([]);
      return;
    }

    // Filter for 3-digit numbers matching search input
    let filtered = availableDigits.filter(digit => {
      const digitStr = digit.toString();
      return digitStr.length === 3 && /^\d+$/.test(digitStr);
    });

    filtered = filtered
      .filter(digit => digit.toString().includes(searchInput.trim()))
      .slice(0, 10);

    setFilteredDigits(filtered);
  }, [searchInput, availableDigits]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDigitSelect = (digit) => {
    setSelectedDigit(digit);
    setSearchInput('');
    setShowDropdown(false);
  };

  const isMarketClosed = () => {
    const closeTimeStr = closeTime;
    if (!closeTimeStr) return false;
    const now = new Date();
    const kolkataTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    const currentTimeInMinutes = kolkataTime.getHours() * 60 + kolkataTime.getMinutes();
    const [ch, cm] = closeTimeStr.split(':').map(Number);
    return currentTimeInMinutes >= ch * 60 + cm - 5;
  };

  const handlePlaceBid = async () => {
    if (isMarketClosed()) {
      toast.error('Market Closed');
      navigate('/starline');
      return;
    }
    if (!selectedDigit || !points.trim()) {
      toast.error('Please select a number and enter points');
      return;
    }

    try {
      setLoading(true);
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const playerId = userData?.id || '';

      if (!playerId) {
        toast.error('Player ID not found. Please login again.');
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append('game_id', gameId);
      formData.append('starline_game_type_id', gameType);
      formData.append('player_id', playerId);
      formData.append('bid_amount', points);
      formData.append('bid_number', selectedDigit);

      const response = await fetch('https://backend.mainbazar777.site/api/starline-play-game', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Bid placed successfully:', data);
        toast.success(`Bid placed successfully! ₹${points}`);
        setShowEntryAdded(true);
        setTimeout(() => setShowEntryAdded(false), 2000);

        // Refresh wallet balance after successful bid
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        if (userData?.id) {
          updateWalletBalance(userData.id);
        }

        // Clear form
        setSelectedDigit('');
        setPoints('');
        setSearchInput('');
      } else {
        toast.error('Failed to place bid');
      }
    } catch (err) {
      console.error('Error placing bid:', err);
      toast.error('An error occurred while placing bid');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${String(displayHour).padStart(2, '0')}:${minutes} ${ampm}`;
  };

  const today = new Date();
  const dateString = `${today.getDate()}-${today.getMonth() + 1}-${today.getFullYear()}`;

  if (loading) {
    return (
      <div className="game-entry">
        <header className="game-entry-header">
          <button className="back-btn" onClick={onBack}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h1 className="game-title">Loading...</h1>
          <div style={{ width: '24px' }}></div>
        </header>
        <div className="game-content">
          <div className="loading-message">Loading game data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="game-entry">
        <header className="game-entry-header">
          <button className="back-btn" onClick={onBack}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h1 className="game-title">Error</h1>
          <div style={{ width: '24px' }}></div>
        </header>
        <div className="game-content">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="game-entry">
      <header className="game-entry-header">
        <button className="back-btn" onClick={onBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h1 className="game-title">{gameName || gameTypeLabel}</h1>
        <button className="wallet-btn" onClick={() => navigate("/funds")}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="6" width="20" height="14" rx="2" stroke="white" strokeWidth="2"/>
            <path d="M2 10H22" stroke="white" strokeWidth="2"/>
            <circle cx="18" cy="15" r="1.5" fill="white"/>
          </svg>
          <span className="wallet-balance">₹ {walletBalance}</span>
        </button>
      </header>

      <div className="game-content">
        {/* Game Info Section - No Status for Starline */}
        <div className="game-info-section">
          <h2 className="game-type-heading">{gameTypeLabel}</h2>
          <div className="info-row">
            <div className="info-item">
              <label>Date</label>
              <input type="text" value={dateString} readOnly disabled />
            </div>
          </div>

          <div className="time-section">
            <div className="time-item">
              <span className="time-label">Time Open:</span>
              <span className="time-value">{formatTime(openTime)}</span>
            </div>
            <div className="time-item">
              <span className="time-label">Time Close:</span>
              <span className="time-value">{formatTime(closeTime)}</span>
            </div>
          </div>
        </div>

        <div className="input-section">
          <div className="input-row">
            <div className="input-item" ref={dropdownRef}>
              <label>Select Panna (3 Digits):</label>
              <div className="dropdown-container">
                <input
                  type="text"
                  placeholder="Enter Single Panna (3 digits only)..."
                  value={searchInput || selectedDigit}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow digits and max 3 characters
                    if (/^\d{0,3}$/.test(value)) {
                      setSearchInput(value);
                      setShowDropdown(true);
                      if (!value) {
                        setSelectedDigit('');
                      }
                    }
                  }}
                  onFocus={() => setShowDropdown(true)}
                  className="digit-input"
                  autoComplete="off"
                  maxLength="3"
                />
                {showDropdown && filteredDigits.length > 0 && (
                  <div className="dropdown-menu">
                    {filteredDigits.map((digit, index) => (
                      <div
                        key={index}
                        className="dropdown-item"
                        onClick={() => handleDigitSelect(digit)}
                      >
                        {digit}
                      </div>
                    ))}
                  </div>
                )}
                {showDropdown && searchInput && filteredDigits.length === 0 && (
                  <div className="dropdown-menu">
                    <div className="dropdown-item disabled">No results found</div>
                  </div>
                )}
              </div>
            </div>
            <div className="input-item">
              <label>Enter Funds:</label>
              <input
                type="number"
                placeholder="Funds"
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                className="points-input"
              />
            </div>
          </div>

          <button className="add-btn" onClick={handlePlaceBid}>
            Place Bid
          </button>
        </div>
      </div>

      {showEntryAdded && (
        <div className="bid-placed-notification">
          ✓ Bid Placed Successfully
        </div>
      )}
    </div>
  );
}

export default StarlineSinglePannaGame;
